import { dist, median, v3, type Vec3 } from '../core/math';
import { LM, type Side, type Stance } from './landmarks';
import type { BodyFrame } from './types';

/**
 * Per-user calibration captured from a short "hold your guard" hold.
 *
 * This is the single most important reliability lever in the whole system, and
 * the reason a hand-rolled recogniser can beat a generic action-classifier here:
 * a classifier has to generalise over every body, camera and stance it might
 * meet, whereas we get to *measure* this body, this camera, this stance once and
 * then work in a normalised space where thresholds are near-universal.
 */
export interface Calibration {
  done: boolean;
  /** Shoulder width in world units (metres-ish). The unit of everything. */
  shoulderWidth: number;
  /** Shoulder width as a fraction of image width, at the calibration distance. */
  imageShoulderWidth: number;
  /** Full arm length (shoulder->elbow->wrist) per side, in shoulder widths. */
  armLength: Record<Side, number>;
  /** Head centre in body frame while holding guard, sw. The origin for slip/duck. */
  headHome: Vec3;
  /** Each glove's resting position in body frame, sw. */
  guardHome: Record<Side, Vec3>;
  /** Image-space hip-centre x at calibration, used as the lateral origin. */
  imageHipX: number;
  /** Torso yaw while in stance, degrees. Bladed stances are not "wrong". */
  stanceYaw: number;
  /**
   * The world-space z component of the spine vector at rest.
   *
   * This is what makes lean-back detectable at all. Everything else is measured
   * in torso space, which rotates *with* the torso — so a player leaning away
   * from the camera at the waist carries their head with the frame and produces
   * almost no head displacement in torso coordinates. The absolute tilt of the
   * spine relative to the camera is the signal that survives, and it needs a
   * per-user baseline because nobody stands perfectly upright.
   */
  restLeanZ: number;
  stance: Stance;
  /** Assumed horizontal FOV of the webcam, radians. Only affects the depth proxy. */
  hFovRad: number;
  /** Pinhole focal length in frame-width units, derived from hFovRad. */
  focal: number;
  /** Distance from camera at calibration time, in shoulder widths. */
  homeDistance: number;
  samples: number;
}

export const DEFAULT_HFOV = (62 * Math.PI) / 180;

export function emptyCalibration(): Calibration {
  const focal = 0.5 / Math.tan(DEFAULT_HFOV / 2);
  return {
    done: false,
    shoulderWidth: 0.4,
    imageShoulderWidth: 0.25,
    armLength: { left: 1.55, right: 1.55 },
    headHome: v3(0, 1.62, 0.02),
    guardHome: { left: v3(-0.38, 1.45, 0.3), right: v3(0.38, 1.45, 0.3) },
    imageHipX: 0.5,
    stanceYaw: 0,
    restLeanZ: 0,
    stance: 'orthodox',
    hFovRad: DEFAULT_HFOV,
    focal,
    homeDistance: focal / 0.25,
    samples: 0,
  };
}

interface Sample {
  shoulderWidth: number;
  imageShoulderWidth: number;
  armL: number;
  armR: number;
  head: Vec3;
  handL: Vec3;
  handR: Vec3;
  hipX: number;
  yaw: number;
  leanZ: number;
  ankleFwdL: number;
  ankleFwdR: number;
  wristFwdL: number;
  wristFwdR: number;
}

/**
 * Accumulates samples during the guard hold and reduces them with a median, not
 * a mean: a single frame where the model flips a limb behind the body would drag
 * a mean badly, and those flips are common at the start of tracking.
 */
export class CalibrationCollector {
  private samples: Sample[] = [];
  /** Frames in a row where the pose was steady enough to count. */
  steadyFrames = 0;

  get count(): number {
    return this.samples.length;
  }

  reset(): void {
    this.samples.length = 0;
    this.steadyFrames = 0;
  }

  add(world: Vec3[], image: Vec3[], body: Vec3[], frame: BodyFrame): void {
    const sw = frame.scale;
    if (sw <= 1e-4) return;
    const armL = (dist(world[LM.LEFT_SHOULDER], world[LM.LEFT_ELBOW]) + dist(world[LM.LEFT_ELBOW], world[LM.LEFT_WRIST])) / sw;
    const armR =
      (dist(world[LM.RIGHT_SHOULDER], world[LM.RIGHT_ELBOW]) + dist(world[LM.RIGHT_ELBOW], world[LM.RIGHT_WRIST])) / sw;

    this.samples.push({
      shoulderWidth: sw,
      imageShoulderWidth: Math.abs(image[LM.LEFT_SHOULDER].x - image[LM.RIGHT_SHOULDER].x),
      armL,
      armR,
      head: { ...midOf(body[LM.LEFT_EAR], body[LM.RIGHT_EAR]) },
      handL: { ...body[LM.LEFT_WRIST] },
      handR: { ...body[LM.RIGHT_WRIST] },
      hipX: (image[LM.LEFT_HIP].x + image[LM.RIGHT_HIP].x) * 0.5,
      yaw: frame.yaw,
      leanZ: frame.up.z,
      ankleFwdL: body[LM.LEFT_ANKLE].z,
      ankleFwdR: body[LM.RIGHT_ANKLE].z,
      wristFwdL: body[LM.LEFT_WRIST].z,
      wristFwdR: body[LM.RIGHT_WRIST].z,
    });
    if (this.samples.length > 240) this.samples.shift();
  }

  /** True once we have enough steady frames to trust the medians (~2 s at 30 fps). */
  get ready(): boolean {
    return this.samples.length >= 45;
  }

  finish(hFovRad = DEFAULT_HFOV): Calibration {
    const c = emptyCalibration();
    if (this.samples.length === 0) return c;
    const pick = <K extends keyof Sample>(k: K) => median(this.samples.map((s) => s[k] as number));
    const pickVec = (k: 'head' | 'handL' | 'handR'): Vec3 => ({
      x: median(this.samples.map((s) => s[k].x)),
      y: median(this.samples.map((s) => s[k].y)),
      z: median(this.samples.map((s) => s[k].z)),
    });

    c.shoulderWidth = pick('shoulderWidth');
    c.imageShoulderWidth = Math.max(0.05, pick('imageShoulderWidth'));
    c.armLength = { left: pick('armL'), right: pick('armR') };
    c.headHome = pickVec('head');
    c.guardHome = { left: pickVec('handL'), right: pickVec('handR') };
    c.imageHipX = pick('hipX');
    c.stanceYaw = pick('yaw');
    c.restLeanZ = pick('leanZ');
    c.hFovRad = hFovRad;
    c.focal = 0.5 / Math.tan(hFovRad / 2);
    c.homeDistance = c.focal / c.imageShoulderWidth;

    // Stance: the lead side is the one whose foot AND hand sit further forward.
    // Feet are the stronger signal when visible, but legs are frequently cropped
    // out of a desk webcam, so hands get a real vote rather than a tie-break.
    const footBias = pick('ankleFwdL') - pick('ankleFwdR');
    const handBias = pick('wristFwdL') - pick('wristFwdR');
    const footVisible = Math.abs(pick('ankleFwdL')) > 1e-4 || Math.abs(pick('ankleFwdR')) > 1e-4;
    const bias = (footVisible ? footBias * 0.6 : 0) + handBias * 0.4;
    c.stance = bias > 0 ? 'orthodox' : 'southpaw';

    c.done = true;
    c.samples = this.samples.length;
    return c;
  }
}

function midOf(a: Vec3, b: Vec3): Vec3 {
  return { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5, z: (a.z + b.z) * 0.5 };
}

const STORAGE_KEY = 'vs.calibration.v1';

export function saveCalibration(c: Calibration): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* private browsing — calibration is cheap to redo */
  }
}

export function loadCalibration(): Calibration | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Calibration;
    return c?.done ? { ...emptyCalibration(), ...c } : null;
  } catch {
    return null;
  }
}
